import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { catchError, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash-es';

import { AppService } from '../../../../../app.service';
import { GlobalService } from '../../../../../global/services/global.service';
import { GtwSnackbarService } from '../../../../../shared/_services/gtw-snackbar.service';
import { MasterDataService } from '../../master-data.service';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../../../shared/utils/appkit-dropdown.util';

interface FormLineMapping {
  form: any;
  schedule: any;
  line: any;
  column: any;
  section: any;
  part: any;
  form_chart_key: any;
  form_seq_key: any;
}

interface ClientItem {
  id: any;
  label: string;
  is_active: 'A' | 'N';
}

type Mode = 'lookup' | 'edit' | 'add' | 'toggleClient';

/**
 * Replicates AngularJS `EditSytemAccountCtrl` + `edit-system-account.html`
 * (system-mapping/edit-system-account-modal — "System-Form Acct Mapping").
 *
 * Top row: Acct Type (readonly, code_desc loaded with action_code 0cz3ni) + System Account display.
 * Action buttons: Add / Activate-Deactivate / Reset.
 * Conditional sections:
 *   - mode 'edit' or 'add'    → cascading form mapping rows (lookup action_code 2losso) + client selector
 *   - mode 'toggleClient'     → multi-select clients + Activate / Deactivate buttons
 *   - mode 'lookup' (default) → only the existing-mappings table is visible
 * Bottom: table of existing mappings (action_code 8n2j2s) with edit icon per row.
 *
 * Save → MasterDataService.editSysAccount.
 * Toggle → MasterDataService.toggleClient.
 */
@Component({
    selector: 'gtw-edit-system-account-modal',
    templateUrl: './edit-system-account-modal.component.html',
    styleUrls: ['./edit-system-account-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EditSystemAccountModalComponent implements OnInit, OnDestroy {
  loading = true;
  sysacctloading = false;
  crudLoading = false;
  submitted = false;

  mode: Mode = 'lookup';
  isToggleClient = false;
  lookUpMapping = false;

  rowData: any = {};
  taxYear: any = '';
  scenario: any = '';

  // Top fields
  code_desc = '';
  CHECK_SYS_ACCT = '';

  // Mapping
  formLineMapping: FormLineMapping[] = [];
  formChartList: any[] = [];

  // Lookups
  loadSystemAcctLookupTemp: any[] = [];
  loadLookupForm: any[] = [];
  scheduleLookup: any[][] = [];
  lineLookup: any[][] = [];
  columnLookup: any[][] = [];
  sectionLookup: any[][] = [];
  partLookup: any[][] = [];

  // Existing mappings
  loadSystemAndLocalAccountData: any[] = [];

  // Client selection (used both for save mapping and for toggleClient)
  isClientSpecific = false;
  clientList: ClientItem[] = [];
  clientDropdownList: ClientItem[] = [];
  selectedClientList: ClientItem[] = [];
  selectedClientList2: ClientItem[] = [];
  starter_ds = '';

  // Edit-state metadata
  oldFormAcctKey: any = '';
  currentEditedIndex = -1;

  // Sorting state
  orderByField = 'FORM';
  reverseSort = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<EditSystemAccountModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private masterDataService: MasterDataService,
    private appService: AppService,
    private globalService: GlobalService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {
    this.rowData = data?.rowData || {};
    this.taxYear = data?.taxYear ?? this.rowData?.TAX_YEAR ?? this.globalService.global?.globalParams?.tax_year ?? '';
    this.scenario = data?.scenario ?? this.globalService.global?.globalParams?.scenario ?? '';
  }

  ngOnInit(): void {
    this.formLineMapping = [this.newFormLine()];
    this.CHECK_SYS_ACCT = `${this.rowData?.ACCT ?? ''} - ${this.rowData?.ACCT_DESC ?? ''}`;
    this.buildClientList();
    this.loadAllLookups();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----------------------------------------------------------------
  // Lookup loaders
  // ----------------------------------------------------------------

  private loadAllLookups(): void {
    this.loading = true;
    this.cdr.detectChanges();

    const safe = (actionCode: string, extra: any = {}) =>
      this.masterDataService.loadJsonObject(actionCode, { tax_year: this.taxYear, ...extra })
        .pipe(catchError(() => of({ callSuccess: '0', jsonObject: [] as any[] })));

    forkJoin({
      formChart: safe('wkl7ky'),
      lookupTemp: safe('2losso', {
        screen_key: this.rowData?.SCREEN_KEY,
        acct_type: this.rowData?.ACCT_TYPES,
      }),
      codeDesc: safe('0cz3ni', {
        scenario: this.scenario,
        code_name: this.rowData?.ACCT_TYPE,
      }),
      mappingsTable: safe('8n2j2s', {
        scenario: this.scenario,
        sys_acct_key: this.rowData?.ACCT_KEY,
      }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.formChartList = res?.formChart?.jsonObject || [];

        // Normalize string fields up-front. Backend returns FORM/SCHEDULE/LINE/...
        // with stray whitespace ("1120PC ", "L "), which breaks strict equality
        // checks when we filter for the cascading dropdowns and when binding a
        // row's pre-selected value to the dropdown options below.
        const lookupTempRaw: any[] = res?.lookupTemp?.jsonObject || [];
        this.loadSystemAcctLookupTemp = lookupTempRaw.map((o: any) => ({
          ...o,
          FORM: this.norm(o?.FORM),
          SCHEDULE: this.norm(o?.SCHEDULE),
          LINE: this.norm(o?.LINE),
          COL: this.norm(o?.COL),
          SECTION: this.norm(o?.SECTION),
          PART: this.norm(o?.PART),
        }));
        this.loadLookupForm = _.uniq(this.loadSystemAcctLookupTemp.map((o: any) => o?.FORM));
        this.refreshFormDropdownItems();

        this.code_desc = res?.codeDesc?.jsonObject?.[0]?.CODE_DESC || '';

        this.loadSystemAndLocalAccountData = res?.mappingsTable?.jsonObject || [];

        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  private fetchSystemAcctData(): void {
    this.sysacctloading = true;
    this.cdr.detectChanges();
    this.masterDataService.loadJsonObject('8n2j2s', {
      tax_year: this.taxYear,
      scenario: this.scenario,
      sys_acct_key: this.rowData?.ACCT_KEY,
    })
      .pipe(takeUntil(this.destroy$), catchError(() => of({ callSuccess: '0', jsonObject: [] as any[] })))
      .subscribe((res: any) => {
        this.loadSystemAndLocalAccountData = res?.jsonObject || [];
        this.sysacctloading = false;
        this.cdr.detectChanges();
      });
  }

  // ----------------------------------------------------------------
  // Mode switching
  // ----------------------------------------------------------------

  formAdd(option?: 'toggleClient'): void {
    this.oldFormAcctKey = '';
    if (option === 'toggleClient') {
      this.isToggleClient = true;
      this.mode = 'toggleClient';
      this.lookUpMapping = false;
    } else {
      this.isToggleClient = false;
      this.mode = 'add';
      this.formLineMapping = [this.newFormLine()];
      this.lookUpMapping = true;
    }
    this.cdr.detectChanges();
  }

  toggleLookup(): void {
    this.formLineMapping = [this.newFormLine()];
    this.lookUpMapping = this.isToggleClient ? false : !this.lookUpMapping;
    this.mode = 'lookup';
    this.isToggleClient = false;
    this.scheduleLookup = [];
    this.lineLookup = [];
    this.columnLookup = [];
    this.sectionLookup = [];
    this.partLookup = [];
    this.cdr.detectChanges();
  }

  formEdit(item: any, index: number): void {
    this.oldFormAcctKey = item?.FORM_ACCT_KEY;
    this.isToggleClient = false;
    this.mode = 'edit';
    this.lookUpMapping = true;
    this.currentEditedIndex = index;

    // Normalize the row values the same way we normalized the lookup data so
    // dropdown values match an option exactly.
    // NB: legacy AngularJS combined LINE with LINE_DESC ("15 - description")
    // when pre-filling the Line dropdown — but the option list contains pure
    // LINE codes ("15", "2a"), so the model never matched and the cascade
    // (column/section/part) silently produced empty option arrays. We bind
    // the raw LINE here.
    const form = this.norm(item?.FORM);
    const schedule = this.norm(item?.SCHEDULE);
    const line = this.norm(item?.LINE);
    const column = this.norm(item?.COL);
    const section = this.norm(item?.SECTION);
    const part = this.norm(item?.PART);

    this.formLineMapping = [{
      form,
      schedule,
      line,
      column,
      section,
      part,
      form_chart_key: item?.TO_CHART_KEY,
      form_seq_key: '',
    }];

    // Pre-fill cascading lookups for the row so each dropdown shows the right
    // options for the saved selection.
    const data = this.loadSystemAcctLookupTemp;
    this.scheduleLookup[0] = _.uniq(
      data.filter((o: any) => o?.FORM === form).map((o: any) => o?.SCHEDULE),
    );
    this.lineLookup[0] = _.uniq(
      data.filter((o: any) => o?.FORM === form && o?.SCHEDULE === schedule)
          .map((o: any) => o?.LINE),
    );
    this.columnLookup[0] = _.uniq(
      data.filter((o: any) => o?.FORM === form && o?.SCHEDULE === schedule && o?.LINE === line)
          .map((o: any) => o?.COL),
    );
    this.sectionLookup[0] = _.uniq(
      data.filter((o: any) => o?.FORM === form && o?.SCHEDULE === schedule
                  && o?.LINE === line && o?.COL === column)
          .map((o: any) => o?.SECTION),
    );
    this.partLookup[0] = _.uniq(
      data.filter((o: any) => o?.FORM === form && o?.SCHEDULE === schedule
                  && o?.LINE === line && o?.COL === column && o?.SECTION === section)
          .map((o: any) => o?.PART),
    );

    // Fallback: if the lookup template (action_code 2losso) doesn't contain the
    // exact combination saved in the mappings table, the cascade above produces
    // empty option lists and the dropdown silently falls back to placeholder.
    // To make the saved row's values always visible, we inject them into the
    // option arrays. The user can still pick any other valid option from the
    // lookup-derived list.
    this.ensureOption(this.scheduleLookup[0], schedule);
    this.ensureOption(this.lineLookup[0], line);
    this.ensureOption(this.columnLookup[0], column);
    this.ensureOption(this.sectionLookup[0], section);
    this.ensureOption(this.partLookup[0], part);

    this.cdr.detectChanges();
  }

  /** Trim a raw lookup value to a plain string suitable for strict equality. */
  private norm(v: any): string {
    return (v ?? '').toString().trim();
  }

  /** Ensure a saved value is present as a selectable option, even when the
   * lookup template doesn't include it. Mutates `arr` in place. */
  private ensureOption(arr: any[], val: string): void {
    if (val && Array.isArray(arr) && !arr.includes(val)) {
      arr.unshift(val);
    }
  }

  // ----------------------------------------------------------------
  // Cascading dropdowns (same logic as Create modal)
  // ----------------------------------------------------------------

  onFormChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.schedule = ''; row.line = ''; row.column = ''; row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter((o: any) => o?.FORM == row.form);
    this.scheduleLookup[idx] = _.uniq(filtered.map((o: any) => o?.SCHEDULE));
    this.lineLookup[idx] = [];
    this.columnLookup[idx] = [];
    this.sectionLookup[idx] = [];
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onScheduleChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.line = ''; row.column = ''; row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule,
    );
    this.lineLookup[idx] = _.uniq(filtered.map((o: any) => o?.LINE));
    this.columnLookup[idx] = [];
    this.sectionLookup[idx] = [];
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onLineChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.column = ''; row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule && o?.LINE == row.line,
    );
    this.columnLookup[idx] = _.uniq(filtered.map((o: any) => o?.COL));
    this.sectionLookup[idx] = [];
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onColumnChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule
        && o?.LINE == row.line && o?.COL == row.column,
    );
    this.sectionLookup[idx] = _.uniq(filtered.map((o: any) => o?.SECTION));
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onSectionChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule
        && o?.LINE == row.line && o?.COL == row.column && o?.SECTION == row.section,
    );
    this.partLookup[idx] = _.uniq(filtered.map((o: any) => o?.PART));
    this.cdr.detectChanges();
  }

  addNewFormLineMapping(): void {
    this.formLineMapping.push(this.newFormLine());
    this.cdr.detectChanges();
  }

  // ----------------------------------------------------------------
  // Client list
  // ----------------------------------------------------------------

  private buildClientList(): void {
    const clients = this.appService.USER_SETTINGS?.user?.clients || [];
    clients.forEach((c: any) => {
      if (c.client_key !== 0) {
        this.clientList.push({
          id: c.client_datasource,
          label: c.client_name,
          is_active: 'N',
        });
      } else {
        this.starter_ds = c.client_datasource;
      }
    });
    this.clientList.forEach((client: any) => {
      client.showCheckbox = true;
      client.role = 'checkbox';
    });
    this.clientDropdownList = [...this.clientList];
  }

  onClientFilterResult(event: any): void {
    this.clientDropdownList = event?.result || [];
  }

  removeClient(index: number): void {
    const item = this.selectedClientList[index];
    this.selectedClientList.splice(index, 1);
    if (item) item.is_active = 'N';
    this.cdr.detectChanges();
  }

  removeClient2(index: number): void {
    const item = this.selectedClientList2[index];
    this.selectedClientList2.splice(index, 1);
    if (item) item.is_active = 'N';
    this.cdr.detectChanges();
  }

  // ----------------------------------------------------------------
  // Save / Toggle
  // ----------------------------------------------------------------

  private validateData(): boolean {
    let haveError = false;
    if (this.isClientSpecific && this.selectedClientList.length <= 0) {
      haveError = true;
      this.snackbar.openSnackBar('Please select at least 1 client', 'gtw-snackbar--red');
    }
    for (const formLine of this.formLineMapping) {
      if (!formLine.form || !formLine.schedule || !formLine.column
          || !formLine.section || !formLine.part || !formLine.form_chart_key) {
        haveError = true;
        this.snackbar.openSnackBar('Please select all possible form mapping details', 'gtw-snackbar--red');
        break;
      }
    }
    return haveError;
  }

  private buildSavePayload(): any {
    const sysAcctObj: any = {
      sys_acct_key: this.rowData?.ACCT_KEY,
      old_form_acct_key: this.oldFormAcctKey,
      is_client_specific: this.isClientSpecific ? true : false,
      form_data: this.formLineMapping,
      starter_ds: this.starter_ds,
    };

    const selectedClientsArray: string[] = [];
    if (!sysAcctObj.is_client_specific) {
      this.clientList.forEach((c) => {
        c.is_active = 'A';
        selectedClientsArray.push(c.label);
      });
    } else {
      this.clientList.forEach((c) => {
        if (c.is_active === 'A') {
          selectedClientsArray.push(c.label);
        }
      });
    }
    if (this.selectedClientList.length > 0) {
      this.selectedClientList.forEach((c) => {
        c.is_active = 'A';
        if (!selectedClientsArray.includes(c.label)) {
          selectedClientsArray.push(c.label);
        }
      });
    }
    sysAcctObj.selectedClients = this.clientList.map((client) =>
      this.stripDropdownMetadata(client),
    );
    sysAcctObj.selectedClientsString = selectedClientsArray.join(',');
    return sysAcctObj;
  }

  save(): void {
    if (this.crudLoading) return;
    this.submitted = true;
    if (this.validateData()) return;

    this.crudLoading = true;
    this.cdr.detectChanges();

    const payload = this.buildSavePayload();

    this.masterDataService
      .editSysAccount(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.crudLoading = false;
          if (data?.callSuccess == 1 || data?.callSuccess === '1') {
            this.snackbar.openSnackBar('Form mapping updated successfully', 'gtw-snackbar--green');
            this.toggleLookup();
            this.isClientSpecific = false;
            this.fetchSystemAcctData();
            this.dialogRef?.close('saved');
          } else {
            const failed = Array.isArray(data?.failedCients)
              ? data.failedCients.join(',')
              : (data?.failedCients || '');
            const msg = failed
              ? `Form mapping failed for ${failed}. Please try again after sometime`
              : (data?.errorMessage || 'Save failed');
            this.snackbar.openSnackBar(msg, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error saving system account', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  toggleClient(action: 'A' | 'N'): void {
    if (this.selectedClientList2.length <= 0) {
      this.snackbar.openSnackBar('Please select at least 1 client', 'gtw-snackbar--red');
      return;
    }

    const selectedClientsArray: string[] = [];
    this.selectedClientList2.forEach((c) => {
      c.is_active = action;
      selectedClientsArray.push(c.label);
    });

    const jsonObj: any = {
      sys_acct_key: this.rowData?.ACCT_KEY,
      is_active: action,
      starter_ds: this.starter_ds,
      selectedClients: this.selectedClientList2.map((client) =>
        this.stripDropdownMetadata(client),
      ),
      selectedClientsString: selectedClientsArray.join(','),
    };

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.masterDataService
      .toggleClient(jsonObj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.crudLoading = false;
          if (data?.callSuccess == 1 || data?.callSuccess === '1') {
            this.snackbar.openSnackBar('Action completed', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            const failed = Array.isArray(data?.failedCients)
              ? data.failedCients.join(',')
              : (data?.failedCients || '');
            const msg = failed
              ? `Request failed for ${failed}. Please try again after sometime`
              : (data?.errorMessage || 'Request failed. Please try again after sometime');
            this.snackbar.openSnackBar(msg, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error processing request', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private stripDropdownMetadata(client: ClientItem): ClientItem {
    const data: any = { ...client };
    delete data.showCheckbox;
    delete data.role;
    return data as ClientItem;
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------

  private newFormLine(): FormLineMapping {
    return {
      form: '', schedule: '', line: '', column: '',
      section: '', part: '', form_chart_key: '', form_seq_key: '',
    };
  }

  setSort(field: string): void {
    if (this.orderByField === field) {
      this.reverseSort = !this.reverseSort;
    } else {
      this.orderByField = field;
      this.reverseSort = false;
    }
    this.loadSystemAndLocalAccountData = _.orderBy(
      this.loadSystemAndLocalAccountData,
      [field],
      [this.reverseSort ? 'desc' : 'asc'],
    );
    this.cdr.detectChanges();
  }

  formLookupItems: any[] = [];
  formChartItems: any[] = [];

  private refreshFormDropdownItems(): void {
    this.formLookupItems = (this.loadLookupForm || []).map((value) => ({ value, label: String(value ?? '') }));
    this.formChartItems = mapToAppkitDropdownItems(this.formChartList, 'FORM_CHART_CODE', 'FORM_CHART_KEY');
  }

  getRowLookupItems(values: any[] | undefined): any[] {
    return (values || []).map((value) => ({ value, label: String(value ?? '') }));
  }

  getRowSelectedItem(values: any[] | undefined, current: any): any {
    return findAppkitItemByValue(this.getRowLookupItems(values), current);
  }

  onRowPrimitiveSelect(event: any, row: FormLineMapping, field: keyof FormLineMapping, idx: number): void {
    const selected = getAppkitDropdownSelection(event);
    (row as any)[field] = selected?.value ?? '';
    if (field === 'form') this.onFormChange(idx);
    else if (field === 'schedule') this.onScheduleChange(idx);
    else if (field === 'line') this.onLineChange(idx);
    else if (field === 'column') this.onColumnChange(idx);
    else if (field === 'section') this.onSectionChange(idx);
    this.cdr.detectChanges();
  }

  onFormChartSelect(event: any, row: FormLineMapping): void {
    const selected = getAppkitDropdownSelection(event);
    row.form_chart_key = selected?.value ?? '';
    this.cdr.detectChanges();
  }

  findDropdownItemByValue(items: any[], value: any): any {
    return findAppkitItemByValue(items, value);
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  onDropdownSelectItem(event: any): void {
    getAppkitDropdownSelection(event);
  }
}
